/**
 * Team Hook - Fetching and managing team member data
 * @file src/web/features/team/hooks/useTeam.ts
 */

import { useState, useEffect } from 'react';
import { config } from '../../auth/config';

export interface TeamMember {
  id: number;
  name: string;
  position: string;
  qualification?: string | null;
  specialization?: string | null;
  experience?: string | null;
  bio?: string | null;
  imageKey?: string | null;
  imageUrl?: string | null;
  imageUrlExpiry?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedIn?: string | null;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const useTeam = (includeInactive = false) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = includeInactive ? '/api/team/admin' : '/api/team';
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };

      if (includeInactive && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${config.api.baseUrl}${endpoint}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch team members');
      }

      const data = await response.json();
      setMembers(data.members || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching team members:', err);
    } finally {
      setLoading(false);
    }
  };

  const createMember = async (memberData: Partial<TeamMember>) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/team`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(memberData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create team member');
    }

    const data = await response.json();
    setMembers(prev => [...prev, data.member]);
    return data.member;
  };

  const updateMember = async (id: number, memberData: Partial<TeamMember>) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/team/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(memberData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update team member');
    }

    const data = await response.json();
    setMembers(prev => prev.map(m => m.id === id ? data.member : m));
    return data.member;
  };

  const deleteMember = async (id: number) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/team/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete team member');
    }

    setMembers(prev => prev.filter(m => m.id !== id));
  };

  const uploadImage = async (id: number, file: File) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${config.api.baseUrl}/api/team/${id}/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload image');
    }

    const data = await response.json();
    setMembers(prev => prev.map(m => m.id === id ? data.member : m));
    return data.member;
  };

  const deleteImage = async (id: number) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/team/${id}/image`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete image');
    }

    const data = await response.json();
    setMembers(prev => prev.map(m => m.id === id ? data.member : m));
    return data.member;
  };

  useEffect(() => {
    fetchMembers();
  }, [includeInactive]);

  return {
    members,
    loading,
    error,
    refetch: fetchMembers,
    createMember,
    updateMember,
    deleteMember,
    uploadImage,
    deleteImage
  };
};

export const useTeamMember = (id: number) => {
  const [member, setMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMember = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${config.api.baseUrl}/api/team/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch team member');
      }

      const data = await response.json();
      setMember(data.member);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching team member:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMember();
  }, [id]);

  return {
    member,
    loading,
    error,
    refetch: fetchMember
  };
};
